// import node module libraries
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Image, Card, Badge } from "react-bootstrap";
import { numberWithCommas, formatDate, getTotalRating } from "helper/utils";

const CourseCard = ({ course, link, extraclass }) => {
  // Fallback image if course image is missing
  const courseImage = course?.introImage?.[0] || '';
  
  // Get course category/tag for badge
  // const primaryTag = course?.tags?.[0] || "Course";
  
  // Format course duration if available
  const duration = course?.duration || null;
  
  // Get instructor name
  const instructor = course?.instructor?.name || course?.instructorName || "Hope Arthritis Academy";
  
  // Get enrollment count
  const enrollmentCount = course?.enrollmentCount || course?.students || 0;
  
  // Get rating
  // const rating = course?.rating || 0;

  const reviewCount = course?.coursereviewsSet?.edges?.map((item) => item.node);

  const rating = reviewCount?.length !== 0 ? getTotalRating(reviewCount) : 0;

  // const reviewCount = course?.reviewCount || 0;
  // const reviewCount = course?.coursereviewsSet?.edges?.map((item) => item.node);


  const getLevelStyles = (level) => {
    const l = (level || "Medium").toString().toLowerCase();
    if (["easy", "beginner", "basic"].includes(l)) {
      return { bg: "light-primary", text: "text-primary", label: "Easy" };
    }
    if (["medium", "intermediate"].includes(l)) {
      return { bg: "light-warning", text: "text-warning", label: "Medium" };
    }
    if (["hard", "advanced", "expert"].includes(l)) {
      return { bg: "light-danger", text: "text-danger", label: "Hard" };
    }
    return { bg: "light-secondary", text: "text-dark", label: level || "N/A" };
  };

  const getModeStyles = (mode) => {
    const m = (mode || "").toString().toLowerCase();
    if (m === "online") return { bg: "light-info", text: "text-info", label: "Online" };
    if (m === "physical") return { bg: "light-secondary", text: "text-dark", label: "Physical" };
    if (m === "hybrid") return { bg: "light-primary", text: "text-primary", label: "Hybrid" };
    return { bg: "light-secondary", text: "text-dark", label: "Online" };
  };


  return (
    <Card className={`course-card ${extraclass}`}>
      <Link to={link} className="course-card-link">
        {/* Course Image - Fixed Height */}
        <div className="course-card-image-wrapper">
          <Image 
            fluid
            src={courseImage} 
            alt={course?.title || "Course"} 
            className="course-card-image"
            onError={(e) => {
              e.target.src = "/assets/images/course/course-default.jpg";
            }}
          />
          {/* Category Badge */}
          {/* <div className="course-card-badge">
            <Badge bg="" className="custom-badge">
              {primaryTag}
            </Badge>
          </div> */}
          {/* Hover Overlay */}
          <div className="course-card-overlay">
            <div className="overlay-content">
              <i className="fe fe-eye overlay-icon"></i>
              <span className="overlay-text">View Course</span>
            </div>
          </div>
        </div>

        {/* Course Content */}
        <Card.Body className="course-card-body">
          {/* Course Meta */}
          <div className="course-meta">
            {duration && (
              <span className="meta-item">
                <i className="fe fe-clock me-1"></i>
                {duration}
              </span>
            )}
            {enrollmentCount > 0 && (
              <span className="meta-item">
                <i className="fe fe-users me-1"></i>
                {enrollmentCount} students
              </span>
            )}
          </div>

          {/* Course Title - Fixed Height for 2 lines */}
          <h5 className="course-card-title">
            {course?.title || "Untitled Course"}
          </h5>

          {/* Course Description - Fixed Height */}
          {course?.description && (
            <p className="course-card-description">
              {course?.description.length > 100 
                ? `${course?.description.substring(0, 100)}...` 
                : course?.description}
            </p>
          )}

          {/* Spacer to push footer to bottom */}
          <div className="spacer"></div>

          {/* Instructor */}
          <div className="course-instructor">
            <i className="fe fe-user me-2 instructor-icon"></i>
            <span className="instructor-name">{instructor}</span>
          </div>

          {/* Mode + Level */}
          <div className="course-badges pb-2">
            {(() => {
              const { bg, text, label } = getLevelStyles(course?.dificultyLevel);
              return (
                <>Level:&nbsp;
                <Badge bg={bg} className={`course-pill ${text}`}>
                  {label}
                </Badge>
                </>
              );
            })()}

            {(() => {
              const modeRaw = course?.learningMode ?? course?.learning_mode;
              const { bg, text, label } = getModeStyles(modeRaw);
              return (
                <>&nbsp;|&nbsp;Mode:&nbsp;
                <Badge bg={bg} className={`course-pill ${text}`}>
                  {label}
                </Badge>
                </>
              );
            })()}
          </div>

          {/* Push footer to bottom */}
          <div className="spacer" />

          {/* Footer */}
          <div className="course-footer">
            {rating > 0 && (
              <div className="course-rating">
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`fe fe-star ${star <= rating ? 'star-filled' : 'star-empty'}`}
                    />
                  ))}
                </div>
                <span className="rating-text">
                  {rating} {reviewCount > 0 && `(${reviewCount})`}
                </span>
              </div>
            )}

            <div className="course-cta">
              <span className="cta-text">
                Enroll Now
                <i className="fe fe-arrow-right ms-2"></i>
              </span>
            </div>
          </div>
        </Card.Body>
      </Link>

      <style jsx>{`
        /* Card Container - Fixed Height */
        :global(.course-card) {
          border: none;
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          background: white;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        :global(.course-card:hover) {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
        }

        .course-card-link {
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* Image Section - FIXED HEIGHT */
        .course-card-image-wrapper {
          position: relative;
          width: 100%;
          height: 280px;  
          overflow: hidden;
          background: #1f9faf;
          flex-shrink: 0;
        }

        :global(.course-card-image) {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.4s ease;
        }

        :global(.course-card:hover .course-card-image) {
          transform: scale(1.05);
        }

        /* Category Badge */
        .course-card-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          z-index: 2;
        }

        :global(.custom-badge) {
          background: rgba(246, 133, 33, 0.95) !important;
          color: white !important;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          backdrop-filter: blur(10px);
        }

        /* Hover Overlay */
        .course-card-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, rgba(246, 133, 33, 0.9) 0%, rgba(29, 158, 176, 0.9) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 1;
        }

        :global(.course-card:hover) .course-card-overlay {
          opacity: 1;
        }

        .overlay-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          transform: translateY(10px);
          transition: transform 0.3s ease;
        }

        :global(.course-card:hover) .overlay-content {
          transform: translateY(0);
        }

        .overlay-icon {
          font-size: 2rem;
          color: white;
        }

        .overlay-text {
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Card Body - Flexible */
        :global(.course-card-body) {
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        /* Course Meta - FIXED HEIGHT */
        .course-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 0.75rem;
          min-height: 22px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          font-size: 0.8125rem;
          color: #64748b;
          font-weight: 500;
        }

        .meta-item i {
          font-size: 0.875rem;
          color: #94a3b8;
        }

        /* Course Title - FIXED HEIGHT for 2 lines */
        .course-card-title {
          font-size: 1.0625rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.4;
          margin-bottom: 0.75rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: calc(1.0625rem * 1.4 * 2);
          max-height: calc(1.0625rem * 1.4 * 2);
          transition: color 0.3s ease;
        }

        :global(.course-card:hover) .course-card-title {
          color: #F68521;
        }

        /* Course Description - FIXED HEIGHT for 3 lines */
        .course-card-description {
          font-size: 0.875rem;
          color: #64748b;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: calc(0.875rem * 1.5 * 3);
          max-height: calc(0.875rem * 1.5 * 3);
          margin-bottom: 0.75rem;
        }

        /* Spacer - Pushes footer to bottom */
        .spacer {
          flex: 1;
        }

        /* Instructor - Always at same position */
        .course-instructor {
          display: flex;
          align-items: center;
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
          border-top: 1px solid #f1f5f9;
        }

        .instructor-icon {
          font-size: 1rem;
          color: #94a3b8;
        }

        .instructor-name {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Course Footer - Always at bottom */
        .course-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 0.75rem;
          border-top: 1px solid #f1f5f9;
        }

        /* Rating */
        .course-rating {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .rating-stars {
          display: flex;
          gap: 2px;
        }

        .rating-stars i {
          font-size: 0.875rem;
        }

        .star-filled {
          color: #fbbf24;
        }

        .star-empty {
          color: #e2e8f0;
        }

        .rating-text {
          font-size: 0.8125rem;
          color: #64748b;
          font-weight: 600;
          margin-left: 4px;
        }

        /* CTA */
        .course-cta {
          display: flex;
          align-items: center;
        }

        .cta-text {
          font-size: 0.875rem;
          font-weight: 600;
          color: #F68521;
          display: flex;
          align-items: center;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        :global(.course-card:hover) .cta-text {
          color: #1d9eb0;
        }

        .cta-text i {
          font-size: 0.875rem;
          transition: transform 0.3s ease;
        }

        :global(.course-card:hover) .cta-text i {
          transform: translateX(4px);
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .course-card-image-wrapper {
            height: 180px;
          }
        }

        @media (max-width: 768px) {
          .course-card-image-wrapper {
            height: 160px;
          }

          :global(.course-card-body) {
            padding: 1rem;
          }

          .course-card-title {
            font-size: 1rem;
            min-height: calc(1rem * 1.4 * 2);
            max-height: calc(1rem * 1.4 * 2);
          }

          .course-card-description {
            font-size: 0.8125rem;
            min-height: calc(0.8125rem * 1.5 * 3);
            max-height: calc(0.8125rem * 1.5 * 3);
          }

          .course-footer {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
        }
      `}</style>
    </Card>
  );
};

// Specifies the default values for props
CourseCard.defaultProps = {
  course: {},
  link: "#",
  extraclass: "",
};

// Typechecking With PropTypes
CourseCard.propTypes = {
  course: PropTypes.object,
  link: PropTypes.string,
  extraclass: PropTypes.string,
};

export default CourseCard;